import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Input,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { useDeferredValue, useState } from "react"

import PlatformShell from "../../../components/PlatformAdmin/PlatformShell"
import { listPlatformTenants } from "../../../client/platform"

export const Route = createFileRoute("/platform/spaces/")({
  component: PlatformSpacesPage,
})

function PlatformSpacesPage() {
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState("")
  const deferredSearch = useDeferredValue(search)

  const tenantsQuery = useQuery({
    queryKey: ["platformAdmin", "tenants", deferredSearch, status],
    queryFn: () => listPlatformTenants({ search: deferredSearch || undefined, status: status || undefined }),
  })
  const tenants = tenantsQuery.data?.data ?? []
  const tenantsCount = tenantsQuery.data?.count ?? tenants.length

  return (
    <PlatformShell
      title="Spaces"
      description="Lista wszystkich utworzonych przestrzeni SaaS z podstawowym stanem runtime."
    >
      <Stack spacing={5}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Input
            placeholder="Search by company, subdomain or admin email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="archived">Archived</option>
          </Select>
        </SimpleGrid>

        {tenantsQuery.isLoading ? (
          <Stack spacing={3}>
            <Skeleton h="48px" />
            <Skeleton h="48px" />
            <Skeleton h="48px" />
          </Stack>
        ) : null}

        {tenantsQuery.isError ? (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <AlertDescription>Failed to load spaces.</AlertDescription>
          </Alert>
        ) : null}

        {tenantsQuery.data ? (
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Company</Th>
                  <Th>Subdomain</Th>
                  <Th>Status</Th>
                  <Th>Plan</Th>
                  <Th isNumeric>Active users</Th>
                  <Th isNumeric>Total users</Th>
                  <Th>Last activity</Th>
                  <Th>Runtime</Th>
                  <Th>Created</Th>
                </Tr>
              </Thead>
              <Tbody>
                {tenants.map((tenant) => (
                  <Tr key={tenant.id}>
                    <Td>
                      <Link
                        to="/platform/spaces/$tenantId"
                        params={{ tenantId: tenant.id }}
                        style={{ fontWeight: 600, color: "inherit" }}
                      >
                        {tenant.name || tenant.subdomain}
                      </Link>
                      <Text fontSize="xs" color="gray.500">{tenant.initial_admin_email || "No admin email"}</Text>
                    </Td>
                    <Td>{tenant.subdomain}</Td>
                    <Td>
                      <Badge colorScheme={tenant.lifecycle_status === "active" ? "green" : tenant.lifecycle_status === "suspended" ? "red" : tenant.lifecycle_status === "archived" ? "gray" : "yellow"}>
                        {tenant.lifecycle_status || (tenant.status ? "active" : "inactive")}
                      </Badge>
                    </Td>
                    <Td>{tenant.plan_code || "free"}</Td>
                    <Td isNumeric>{tenant.active_users_count}</Td>
                    <Td isNumeric>{tenant.usage_snapshot?.total_users_count ?? 0}</Td>
                    <Td>{formatMaybeDate(tenant.usage_snapshot?.last_user_seen_at)}</Td>
                    <Td>
                      <Badge colorScheme={tenant.runtime_healthy ? "green" : "orange"}>
                        {tenant.runtime_healthy ? "healthy" : "issue"}
                      </Badge>
                    </Td>
                    <Td>{new Date(tenant.created_at).toLocaleString()}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Text mt={4} fontSize="sm" color="gray.500">
              Total: {tenantsCount}
            </Text>
          </Box>
        ) : null}
      </Stack>
    </PlatformShell>
  )
}

function formatMaybeDate(value?: string | null) {
  if (!value) return "n/a"
  return new Date(value).toLocaleString()
}
